import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Box,
} from '@mui/material';
import usePlacesAutocomplete, {
  getGeocode,
  getLatLng,
} from 'use-places-autocomplete';

function AddressBarComponent({
  name,
  question,
  onChange,
  onNext,
  values,
  setValidationState,
  registerValidation,
  inputRef,
  nextFieldRef,
  isMobile,
  onLocationResolved,
  layoutVariant = 'default',
  readOnly = false,
}) {
  const isAccountLayout = layoutVariant === 'account';
  const {
    ready,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    debounce: 300,
    requestOptions: { componentRestrictions: { country: 'za' } },
  });

  const initialValue = values?.[name] || question.value || '';
  const [inputValue, setInputValue] = useState(initialValue);
  const [error, setError] = useState('');
  const [focused, setFocused] = useState(false);
  const wrapperRef = useRef(null);
  const mousedownRef = useRef(false);
  const [dropdownPosition, setDropdownPosition] = useState(null);

  useEffect(() => {
    registerValidation?.(name, triggerValidation);
  }, [inputValue]);

  useEffect(() => {
    if (question.value && question.value !== inputValue) {
      setInputValue(question.value);
      setValue(question.value, false);
    }
  }, [question.value]);

  useEffect(() => {
    const handleMouseDown = () => (mousedownRef.current = true);
    const handleMouseUp = () => (mousedownRef.current = false);
    window.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  const validate = (input = inputValue) => {
    const isValid = !!input;
    if (!isValid && question.validation?.required) {
      setError(question.validation.message || 'Required');
    } else {
      setError('');
    }
    setValidationState?.(name, isValid);
    return isValid;
  };
  const triggerValidation = () => validate();

  const handleClear = (e) => {
    e.preventDefault();
    setInputValue('');
    setValue('', false);
    clearSuggestions();
    setError('');
    onChange?.(name, '');
    if (inputRef?.current) inputRef.current.focus();
  };

  const handleInput = (e) => {
    const input = e.target.value;
    setInputValue(input);
    setValue(input);
    onChange?.(name, input);
    validate(input);
  };

  const handleFocus = () => {
    setFocused(true);
    setTimeout(() => {
      const rect = wrapperRef.current?.getBoundingClientRect();
      const width = wrapperRef.current?.offsetWidth;
      if (rect && width) {
        setDropdownPosition({
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX,
          width,
        });
      }
    }, 10);
  };

  const handleBlur = () => {
    setTimeout(() => {
      if (!mousedownRef.current) setFocused(false);
    }, 0);
  };

  const handleSelect = async (address) => {
    setInputValue(address);
    setValue(address, false);
    clearSuggestions();
    onChange?.(name, address);
    validate(address);

    if (nextFieldRef?.current) nextFieldRef.current.focus();
    else onNext?.();

    setFocused(false);

    try {
      const results = await getGeocode({ address });
      const { lat, lng } = await getLatLng(results[0]);
      onLocationResolved?.({ lat, lng }, address, results[0]);
    } catch (err) {
      // ignore
    }
  };

  const handleKeyDown = async (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (status === 'OK' && data.length > 0) {
        handleSelect(data[0].description);
      } else if (validate()) {
        if (nextFieldRef?.current) nextFieldRef.current.focus();
        else onNext?.();
      }
    }
  };

  const sizeOverrides = {
    'extra-small': '219px',
    small: '301px',
    medium: '403px',
    large: '534px',
    half: '500px',
    full: '1000px',
    'vehicle-return': '324px',
    'vehicle-return-full': '662px',
    'vehicle-full': '448px',
    'accounts-half': '416px',
    'accounts-full': '864px',
  };

  const fieldWidth = question.customSize
    ? sizeOverrides[question.customSize] || '100%'
    : '100%';
  const responsiveWidth = isAccountLayout
    ? '100%'
    : isMobile
      ? '80vw'
      : fieldWidth;

  const dropdownActive = focused && status === 'OK';

  // Hide placeholder when focused or when a value exists (prevents label+placeholder clash)
  const placeholderText =
    focused || inputValue ? '' : question.placeholder || '';

  const dropdown =
    dropdownActive && dropdownPosition
      ? ReactDOM.createPortal(
          <Paper
            elevation={3}
            sx={{
              position: 'absolute',
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: dropdownPosition.width,
              borderRadius: '0 0 20px 20px',
              border: '1px solid #A2ACB1', // ← match border color
              borderTop: 'none',
              boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
              overflowY: 'auto',
              maxHeight: '250px',
              zIndex: 2000,
              '&::-webkit-scrollbar': { display: 'none' },
              msOverflowStyle: 'none',
              scrollbarWidth: 'none',
              pointerEvents: 'auto',
              backgroundColor: '#fff',
            }}
          >
            <List>
              {data.map(({ place_id, description }) => (
                <ListItem key={place_id} disablePadding>
                  <ListItemButton onClick={() => handleSelect(description)}>
                    <ListItemText
                      primary={description}
                      primaryTypographyProps={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '0.875rem',
                        lineHeight: '1.25rem',
                        color: '#162F3B', // value color
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Paper>,
          document.body
        )
      : null;

  return (
    <Box
      sx={{
        width: responsiveWidth,
        maxWidth: '100%',
        mx: isAccountLayout ? 'auto' : isMobile ? 'auto' : undefined,
        boxSizing: 'border-box',
      }}
    >
      <Box
        ref={wrapperRef}
        sx={{
          position: 'relative',
          width: isAccountLayout ? '100%' : isMobile ? '80vw' : '100%',
          maxWidth: isAccountLayout ? '100%' : isMobile ? '100%' : fieldWidth,
          mx: isAccountLayout ? 'auto' : isMobile ? 'auto' : undefined,
        }}
      >
        <TextField
          fullWidth
          placeholder={placeholderText} // ← controlled placeholder
          label={question.label}
          value={inputValue}
          onChange={readOnly ? undefined : handleInput}
          onKeyDown={readOnly ? undefined : handleKeyDown}
          disabled={!readOnly && !ready}
          variant="outlined"
          error={!!error}
          helperText={error}
          inputRef={inputRef}
          onFocus={readOnly ? undefined : handleFocus}
          onBlur={readOnly ? undefined : handleBlur}
          InputLabelProps={{
            sx: {
              color: '#738289', // label color
              // '&.Mui-focused': { color: '#738289' },
              // '&.Mui-error': { color: '#738289' },
            },
          }}
          InputProps={{
            readOnly,
            endAdornment: readOnly ? null : inputValue ? (
              <span
                className="material-symbols-rounded"
                onMouseDown={handleClear}
                style={{
                  fontWeight: 400,
                  fontSize: 20,
                  color: '#738289',
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
              >
                close
              </span>
            ) : (
              <span
                className="material-symbols-rounded"
                style={{ fontWeight: 500, fontSize: 24, color: '#162F3B' }}
              >
                search
              </span>
            ),
          }}
          sx={{
            width: '100%',
            mx: isAccountLayout ? 'auto' : undefined,
            '& .MuiOutlinedInput-root': {
              borderRadius: dropdownActive ? '12px 12px 0 0' : '12px',
              paddingTop: '16px',
              paddingBottom: '16px',
              paddingLeft: '12px',
              paddingRight: '12px',
              fontFamily: 'Inter, sans-serif',
              fontSize: '1rem',
              lineHeight: isMobile ? '100%' : '1.2rem',
              letterSpacing: '-0.02em',

              // Borders: default/hover/focus all #738289
              '& fieldset': {
                borderRadius: dropdownActive ? '12px 12px 0 0' : '12px',
                borderWidth: '1px',
                borderColor: '#A2ACB1',
              },
              // '&:hover fieldset': { borderColor: '#738289' },
              // '&.Mui-focused fieldset': { borderColor: '#738289' },
              // '&.Mui-error fieldset': { borderColor: '#738289' },

              '& input': {
                padding: 0,
                height: 'auto',
                boxSizing: 'border-box',
                fontFamily: 'Inter, sans-serif',
                fontSize: '1rem',
                lineHeight: '1.2rem',
                color: inputValue ? '#162F3B' : '#738289', // value vs empty
                '&::placeholder': {
                  color: '#738289', // placeholder
                  opacity: 1,
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '1rem',
                  lineHeight: '1.2rem',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                },
              },
            },
            '& .MuiFormHelperText-root': {
              textAlign: 'right',
              fontSize: isMobile ? '0.6rem' : '0.75rem',
              color: '#D32F2F',
              fontFamily: 'Inter, sans-serif',
              mt: '0px',
              mx: 0,
              pr: '8px',
            },
          }}
        />
        {dropdown}
      </Box>
    </Box>
  );
}

export default AddressBarComponent;
